/** Paramètres → Portail : ce que le client voit et peut faire. */
import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { CheckField } from "../../components/CheckField";
import { TextArea } from "../../components/TextArea";
import { useCompany } from "../../company/CompanyProvider";
import { portalFromCompany, type CompanyPortal } from "../../portal/types";
import { isCompanyOwner } from "./owner";
import { PortalLinks } from "./PortalLinks";

export function PortalTab() {
  const { company, updatePortal } = useCompany();
  const [draft, setDraft] = useState<CompanyPortal | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!company) return;
    setDraft(portalFromCompany(company));
    setMsg("");
    setErr("");
  }, [company]);

  if (!company || !draft) {
    return <p className="text-sm" style={{ color: "var(--fx-muted)" }}>Aucune société.</p>;
  }

  const owner = isCompanyOwner(company);
  const on = draft.enabled;

  function set<K extends keyof CompanyPortal>(key: K, value: CompanyPortal[K]) {
    setDraft((cur) => (cur ? { ...cur, [key]: value } : cur));
  }

  async function save() {
    if (!draft || !company) return;
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      await updatePortal(company.id, draft);
      setMsg("Enregistré.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fx-co-card">
      <header className="fx-co-card__head">
        <h2>Portail</h2>
        <p>Le client ouvre ses pièces depuis le mail. Logo et couleurs du modèle.</p>
        {!owner ? (
          <p className="fx-co-form__hint">Lecture seule — seul le propriétaire enregistre.</p>
        ) : null}
      </header>
      <div className="fx-fmt">
        <div className="fx-fmt-row">
          <span>Accès</span>
          <div className="flex flex-col gap-2">
            <CheckField
              label="Activer le portail"
              checked={draft.enabled}
              disabled={!owner}
              onChange={(next) => set("enabled", next)}
            />
            <p className="fx-co-form__hint m-0">Sans ça, le bouton du mail n’ouvre rien.</p>
          </div>
        </div>
        <div className="fx-fmt-row fx-fmt-row--top">
          <span>Pièces</span>
          <div className="flex flex-col gap-2">
            <CheckField label="Factures" checked={draft.show_invoices} disabled={!owner || !on} onChange={(next) => set("show_invoices", next)} />
            <CheckField label="Devis" checked={draft.show_quotes} disabled={!owner || !on} onChange={(next) => set("show_quotes", next)} />
            <CheckField label="Avoirs" checked={draft.show_credits} disabled={!owner || !on} onChange={(next) => set("show_credits", next)} />
            <CheckField label="Télécharger le PDF" checked={draft.download_pdf} disabled={!owner || !on} onChange={(next) => set("download_pdf", next)} />
          </div>
        </div>
        <div className="fx-fmt-row fx-fmt-row--top">
          <span>Actions</span>
          <div className="flex flex-col gap-2">
            <CheckField
              label="Payer en ligne"
              checked={draft.can_pay}
              disabled={!owner || !on}
              onChange={(next) => set("can_pay", next)}
            />
            <p className="fx-co-form__hint m-0">Ouvre « Moyen de paiement » dans le portail si GoCardless est connecté. Le client signe le mandat ; vous prélevez ensuite la facture.</p>
            <CheckField
              label="Accepter un devis"
              checked={draft.can_accept_quote}
              disabled={!owner || !on}
              onChange={(next) => set("can_accept_quote", next)}
            />
            <p className="fx-co-form__hint m-0">Ça ouvre un brouillon de facture — jamais une pièce validée toute seule.</p>
          </div>
        </div>
        <div className="fx-fmt-row fx-fmt-row--top">
          <span>Accueil</span>
          <TextArea
            label="Texte (optionnel)"
            rows={3}
            maxLength={400}
            value={draft.welcome}
            disabled={!owner || !on}
            onChange={(e) => set("welcome", e.target.value.slice(0, 400))}
          />
        </div>
        <PortalLinks company={company} enabled={on} owner={owner} />
      </div>
      {err ? <p className="fx-co-form__err">{err}</p> : null}
      {msg ? <p className="fx-co-form__ok">{msg}</p> : null}
      <div className="fx-co-card__foot">
        <Button disabled={busy || !owner} onClick={() => void save()}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
